import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY, GET_COMMUNITIES_IN_GROUP } from '@communities/community/graphql';
import { transformErrors } from '@core/adapters/errors';
import { GET_COMMUNITY_GROUPS_WITH_COMMUNITIES } from '@core/components/Guide/graphql';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityResponse {
  deleteCommunity: {
    success: boolean;
    message?: string;
  };
}

export function useDeleteCommunity(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteCommunityMutation, { loading }] = useMutation<DeleteCommunityResponse>(DELETE_COMMUNITY, {
    refetchQueries: [
      ...(communityGroupId ? [{ query: GET_COMMUNITIES_IN_GROUP, variables: { communityGroupId } }] : []),
      { query: GET_COMMUNITY_GROUPS_WITH_COMMUNITIES },
    ],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteCommunity = async (communityId: string) => {
    try {
      track('DeleteCommunityInitiated', {
        communityId,
      });

      await deleteCommunityMutation({
        variables: { id: communityId },
      });

      track('DeleteCommunitySucceeded', {
        communityId,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityFailed', {
        communityId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteCommunity,
    loading,
    errors,
    resetErrors,
  };
}
